import { useState } from 'react';
import { AdminTokenPackage } from '../../services/token-admin.service';

interface PackageFormModalProps {
  package: AdminTokenPackage | null;
  onSave: (data: Partial<AdminTokenPackage>) => Promise<void>;
  onClose: () => void;
}

export function PackageFormModal({ package: pkg, onSave, onClose }: PackageFormModalProps) {
  const [formData, setFormData] = useState({
    name: pkg?.name || '',
    tokens: pkg?.tokens || 0,
    price: pkg?.price || 0,
    description: pkg?.description || '',
    popular: pkg?.popular || false,
    savings: pkg?.savings || 0,
    isActive: pkg?.isActive !== undefined ? pkg.isActive : true,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    try {
      await onSave(formData);
    } catch (err: any) {
      setError(err.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{pkg ? '✏️ Editar Paquete' : '➕ Nuevo Paquete'}</h3>
          <button onClick={onClose} className="modal-close">✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Nombre del paquete *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Básico, Estándar, Premium"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label>Cantidad de tokens *</label>
                <input
                  type="number"
                  required
                  min={1}
                  value={formData.tokens}
                  onChange={(e) => setFormData({ ...formData, tokens: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="form-group">
                <label>Precio (MXN) *</label>
                <input
                  type="number"
                  required
                  min={0}
                  step={0.01}
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Descripción</label>
              <textarea
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción breve del paquete"
                className="resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label>Ahorro (%)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={formData.savings}
                  onChange={(e) => setFormData({ ...formData, savings: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="form-group flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="popular"
                  checked={formData.popular}
                  onChange={(e) => setFormData({ ...formData, popular: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="popular" className="text-sm font-medium">Marcar como popular</label>
              </div>
            </div>

            <div className="form-group flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="isActive" className="text-sm font-medium">Paquete activo</label>
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">❌</span>
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancelar</button>
            <button type="submit" disabled={isSaving} className="btn btn-primary">
              {isSaving ? 'Guardando...' : pkg ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}